// Mesaje automate (admin): textele trimise automat elevilor — mementourile din chat, notificările
// din aplicație, emailurile. Sus rândul comun (.pageToolbar): canalul, căutarea, starea. Dedesubt
// lista textelor (pe grupe) și editorul celui ales, cu previzualizarea cum îl vede elevul.
// Ciornele nesalvate rămân pe text cât treci la altul (punctul din listă); Ctrl+S salvează.
// Pe telefon / tabletă: lista, iar la alegere editorul cu „Înapoi”.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getMessageTemplates, resetMessageTemplate, updateMessageTemplate } from "../../../api/messageTemplatesService";
import { exampleVars, placeholdersIn, renderMessageText } from "../../../utils/messageTemplatePreview";
import { ButtonUI, CardUI, DropdownUI, InputUI, ListUI, SearchBarUI, StatusUI, TagUI } from "../../UI";

const CHANNELS = [
   {
      id: "chat",
      label: "Chat",
      hint: "Mementourile trimise zilnic la 10:00 în chatul elevului cu managerul; elevul le vede ca mesaje de la manager.",
      preview: "Mesaj în chat",
   },
   {
      id: "notification",
      label: "Notificări",
      hint: "Notificările din aplicație (clopoțelul elevului): titlul și textul fiecăreia.",
      preview: "Notificare",
   },
   {
      id: "email",
      label: "Email",
      hint: "Textele emailurilor; aspectul rămâne același. Un rând gol desparte paragrafele, „- ” face listă, „1. ” listă numerotată.",
      preview: "Email",
   },
];

const STATES = [
   { value: "all", label: "Toate" },
   { value: "custom", label: "Modificate" },
   { value: "default", label: "Implicite" },
   { value: "dirty", label: "Nesalvate" },
];

const fold = (v) =>
   String(v ?? "")
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase();

function formatDate(value) {
   if (!value) return "";
   try {
      return new Date(value).toLocaleString("ro-RO", {
         day: "2-digit",
         month: "2-digit",
         year: "numeric",
         hour: "2-digit",
         minute: "2-digit",
      });
   } catch {
      return "";
   }
}

/** Ce e greșit într-un text, după aceleași reguli ca la salvarea pe server. */
function validationError(template, value) {
   const text = String(value ?? "").trim();
   if (!text) return "Textul nu poate fi gol.";
   if (text.length > template.maxLength) return `Textul poate avea cel mult ${template.maxLength} de caractere.`;
   if (template.kind === "url") {
      return /^https:\/\/[^\s<>"']+$/.test(text) ? "" : "Linkul trebuie să fie o adresă completă care începe cu https://";
   }
   const allowed = new Set((template.placeholders || []).map((p) => p.name));
   const unknown = placeholdersIn(text).filter((name) => !allowed.has(name));
   return unknown.length ? `Eticheta {${unknown[0]}} nu e cunoscută pentru acest text.` : "";
}

// emailul: paragrafele (rând gol), listele („- ”, „1. ”) — ca în emailul trimis
function EmailPreview({ text }) {
   const blocks = String(text || "").split(/\n\s*\n/);
   return blocks.map((block, i) => {
      const lines = block.split("\n").filter((l) => l.trim());
      if (lines.length && lines.every((l) => /^- /.test(l.trim()))) {
         return (
            <ul key={i}>
               {lines.map((l, j) => (
                  <li key={j}>{l.trim().slice(2)}</li>
               ))}
            </ul>
         );
      }
      if (lines.length && lines.every((l) => /^\d+\. /.test(l.trim()))) {
         return (
            <ol key={i}>
               {lines.map((l, j) => (
                  <li key={j}>{l.trim().replace(/^\d+\. /, "")}</li>
               ))}
            </ol>
         );
      }
      return <p key={i}>{block}</p>;
   });
}

export default function MessageTemplatesPage() {
   const [data, setData] = useState(null);
   const [loadError, setLoadError] = useState("");
   const [channel, setChannel] = useState("chat");
   const [query, setQuery] = useState("");
   const [state, setState] = useState("all");
   const [selectedKey, setSelectedKey] = useState(null);
   // pe telefon / tabletă: editorul în locul listei
   const [editing, setEditing] = useState(false);
   const [drafts, setDrafts] = useState({});
   const [busy, setBusy] = useState(false);
   const [feedback, setFeedback] = useState(null);
   const inputRef = useRef(null);

   const load = useCallback(() => {
      setLoadError("");
      getMessageTemplates()
         .then(setData)
         .catch((err) => setLoadError(err?.message || "Nu am putut încărca textele."));
   }, []);

   useEffect(() => {
      load();
   }, [load]);

   const templates = useMemo(() => data?.templates || [], [data]);
   const isDirty = useCallback((t) => drafts[t.key] !== undefined && drafts[t.key] !== t.body, [drafts]);

   const channelOptions = CHANNELS.map((c) => ({
      value: c.id,
      label: `${c.label} · ${templates.filter((t) => t.channel === c.id).length}`,
   }));

   // textele canalului, după căutare și stare
   const inChannel = useMemo(() => templates.filter((t) => t.channel === channel), [templates, channel]);
   const visible = useMemo(() => {
      const words = fold(query).split(/\s+/).filter(Boolean);
      return inChannel.filter((t) => {
         if (state === "custom" && !t.isCustom) return false;
         if (state === "default" && t.isCustom) return false;
         if (state === "dirty" && !isDirty(t)) return false;
         if (!words.length) return true;
         const hay = fold([t.title, t.description, t.group, t.key, drafts[t.key] ?? t.body].join(" "));
         return words.every((w) => hay.includes(w));
      });
   }, [inChannel, query, state, drafts, isDirty]);

   const stateCount = (s) =>
      s.value === "all"
         ? inChannel.length
         : s.value === "custom"
         ? inChannel.filter((t) => t.isCustom).length
         : s.value === "default"
           ? inChannel.filter((t) => !t.isCustom).length
           : templates.filter(isDirty).length;
   const stateOptions = STATES.map((s) => ({ value: s.value, label: `${s.label} · ${stateCount(s)}` }));

   const groups = useMemo(() => {
      const out = [];
      for (const t of visible) {
         let g = out.find((x) => x.name === t.group);
         if (!g) {
            g = { name: t.group, items: [] };
            out.push(g);
         }
         g.items.push(t);
      }
      return out;
   }, [visible]);

   const current = visible.find((t) => t.key === selectedKey) || visible[0] || null;
   const activeChannel = CHANNELS.find((c) => c.id === channel) || CHANNELS[0];

   const choose = (key) => {
      setSelectedKey(key);
      setFeedback(null);
      setEditing(true);
   };

   const setDraft = (key, value) => setDrafts((cur) => ({ ...cur, [key]: value }));
   const dropDraft = (key) =>
      setDrafts((cur) => {
         const next = { ...cur };
         delete next[key];
         return next;
      });

   const replaceTemplate = (updated) => {
      if (!updated) return;
      setData((cur) => (cur ? { ...cur, templates: cur.templates.map((t) => (t.key === updated.key ? updated : t)) } : cur));
      dropDraft(updated.key);
   };

   const value = current ? (drafts[current.key] ?? current.body) : "";
   const dirty = current ? isDirty(current) : false;
   const error = current ? validationError(current, value) : "";
   const placeholders = current?.placeholders || [];

   const save = useCallback(async () => {
      if (!current || !dirty || error || busy) return;
      setBusy(true);
      setFeedback(null);
      try {
         replaceTemplate(await updateMessageTemplate(current.key, drafts[current.key]));
         setFeedback({ tone: "success", text: "Salvat. Următoarele mesaje folosesc textul nou." });
      } catch (err) {
         setFeedback({ tone: "error", text: err?.message || "Nu am putut salva." });
      } finally {
         setBusy(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [current, dirty, error, busy, drafts]);

   const reset = async () => {
      if (!current) return;
      setBusy(true);
      setFeedback(null);
      try {
         replaceTemplate(await resetMessageTemplate(current.key));
         setFeedback({ tone: "success", text: "S-a revenit la textul implicit." });
      } catch (err) {
         setFeedback({ tone: "error", text: err?.message || "Nu am putut reveni la textul implicit." });
      } finally {
         setBusy(false);
      }
   };

   // Ctrl+S / ⌘S: salvează textul deschis
   useEffect(() => {
      const onKey = (e) => {
         if ((e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "S")) {
            e.preventDefault();
            save();
         }
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
   }, [save]);

   // eticheta se pune acolo unde e cursorul (sau la final)
   const insertPlaceholder = (name) => {
      if (!current) return;
      const input = inputRef.current;
      const token = `{${name}}`;
      const start = input?.selectionStart ?? value.length;
      const end = input?.selectionEnd ?? value.length;
      setDraft(current.key, value.slice(0, start) + token + value.slice(end));
      requestAnimationFrame(() => {
         if (!input) return;
         input.focus();
         input.setSelectionRange(start + token.length, start + token.length);
      });
   };

   const preview = current
      ? current.kind === "url"
         ? value
         : renderMessageText(value, exampleVars(placeholders))
      : "";

   return (
      <div className={`adminPage msgTpl${editing ? " is-editing" : ""}`}>
         <div className="pageToolbar">
            <DropdownUI
               size="lg"
               surface="page"
               value={channel}
               onChange={(v) => {
                  setChannel(v);
                  setSelectedKey(null);
                  setEditing(false);
                  setFeedback(null);
               }}
               options={channelOptions}
               aria-label="Canalul"
               className="pageToolbar__menu"
            />
            <SearchBarUI
               surface="page"
               value={query}
               onChange={(e) => setQuery(e.target.value)}
               onClear={() => setQuery("")}
               placeholder="Caută în titluri, descrieri și texte…"
               aria-label="Caută un text"
               className="pageToolbar__search"
            />
            <div className="pageToolbar__controls">
               <DropdownUI
                  size="lg"
                  surface="page"
                  align="end"
                  value={state}
                  onChange={setState}
                  options={stateOptions}
                  aria-label="Starea textelor"
               />
            </div>
         </div>

         {loadError ? (
            <StatusUI look="card" tone="error" action={<ButtonUI onClick={load}>Încearcă din nou</ButtonUI>}>
               {loadError}
            </StatusUI>
         ) : !data ? (
            <StatusUI look="card" tone="loading">
               Se încarcă textele…
            </StatusUI>
         ) : (
            <div className="msgTpl__body">
               <CardUI level="screen" titleSize="sm" title={activeChannel.label} description={activeChannel.hint} className="msgTpl__list">
                  {groups.length ? (
                     groups.map((g) => (
                        <ListUI key={g.name} title={g.name} count={g.items.length}>
                           {g.items.map((t) => (
                              <ListUI.Item
                                 key={t.key}
                                 title={t.title}
                                 detail={t.description}
                                 selected={current?.key === t.key}
                                 highlight={isDirty(t)}
                                 aside={
                                    <TagUI tone={t.isCustom ? "accent" : "muted"} title={t.isCustom && t.updatedAt ? `Modificat ${formatDate(t.updatedAt)}` : undefined}>
                                       {t.isCustom ? "Modificat" : "Implicit"}
                                    </TagUI>
                                 }
                                 onClick={() => choose(t.key)}
                              />
                           ))}
                        </ListUI>
                     ))
                  ) : (
                     <ListUI>
                        <ListUI.Empty>{query || state !== "all" ? "Niciun text pentru filtrele alese." : "Niciun text în acest canal."}</ListUI.Empty>
                     </ListUI>
                  )}
               </CardUI>

               {/* pe telefon / tabletă: înapoi la listă */}
               {current ? (
                  <ButtonUI className="msgTpl__back" onClick={() => setEditing(false)}>
                     ← Toate textele
                  </ButtonUI>
               ) : null}
               {current ? (
                  <CardUI
                     level="screen"
                     className="msgTpl__editor"
                     eyebrow={current.group}
                     title={current.title}
                     description={current.description}
                     actions={
                        <TagUI tone={current.isCustom ? "accent" : "muted"} size="md">
                           {current.isCustom ? `Modificat${current.updatedAt ? ` · ${formatDate(current.updatedAt)}` : ""}` : "Implicit"}
                        </TagUI>
                     }
                  >
                     <InputUI
                        ref={inputRef}
                        as={current.multiline ? "textarea" : "input"}
                        type={current.kind === "url" ? "url" : "text"}
                        label={current.kind === "url" ? "Linkul" : "Textul"}
                        rows={current.channel === "email" ? 8 : 4}
                        value={value}
                        disabled={busy}
                        onChange={(e) => {
                           setDraft(current.key, e.target.value);
                           setFeedback(null);
                        }}
                        error={dirty && error ? error : undefined}
                        className="msgTpl__field"
                     />

                     <div className="msgTpl__meta">
                        {placeholders.length > 0 && (
                           <TagUI.Group className="msgTpl__chips">
                              {placeholders.map((p) => (
                                 <TagUI key={p.name} size="md" onClick={() => insertPlaceholder(p.name)} disabled={busy} title={`Pune ${p.label} (ex.: ${p.example})`}>
                                    {`{${p.name}}`}
                                 </TagUI>
                              ))}
                           </TagUI.Group>
                        )}
                        <span className={`msgTpl__count${value.length > current.maxLength ? " is-over" : ""}`}>
                           {value.length} / {current.maxLength}
                        </span>
                     </div>

                     <div className={`msgTpl__preview msgTpl__preview--${current.kind === "url" ? "url" : current.channel}`}>
                        <span className="msgTpl__previewLabel">
                           {current.kind === "url" ? "Butonul deschide" : `${activeChannel.preview} · cu valori de exemplu`}
                        </span>
                        {current.kind === "url" ? (
                           <a href={preview} target="_blank" rel="noreferrer" className="msgTpl__link">
                              {preview}
                           </a>
                        ) : current.channel === "email" ? (
                           <div className="msgTpl__mail">
                              <EmailPreview text={preview} />
                           </div>
                        ) : (
                           <p className="msgTpl__bubble">{preview}</p>
                        )}
                     </div>

                     {feedback ? <StatusUI tone={feedback.tone}>{feedback.text}</StatusUI> : null}

                     <ButtonUI.Row align="end">
                        {current.isCustom ? (
                           <ButtonUI disabled={busy} confirm="Da, revino" onClick={reset}>
                              Revino la textul implicit
                           </ButtonUI>
                        ) : null}
                        {dirty ? (
                           <ButtonUI disabled={busy} onClick={() => dropDraft(current.key)}>
                              Anulează modificările
                           </ButtonUI>
                        ) : null}
                        <ButtonUI variant="primary" disabled={!dirty || Boolean(error)} loading={busy} onClick={save} title="Ctrl+S">
                           {busy ? "Se salvează…" : "Salvează"}
                        </ButtonUI>
                     </ButtonUI.Row>
                  </CardUI>
               ) : null}
            </div>
         )}
      </div>
   );
}
